import React from 'react';

import { SalesChannelType as SalesChannelTypeEnum } from '@fd/customHooks/useQueryRMSSalesChannels';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import MoreTimeOutlinedIcon from '@mui/icons-material/MoreTimeOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import StoreOutlined from '@mui/icons-material/StoreOutlined';
import Warning from '@mui/icons-material/Warning';
import ZoomInMapOutlinedIcon from '@mui/icons-material/ZoomInMapOutlined';
import { styled } from '@mui/material/styles';
import { salesChannelPermissions } from 'components/SalesChannels/permissions';

import { SettingsMenuItem } from '@fd/ui/List/SettingsList';
import { getSalesChannelType } from '@fd/ui/utils/generateSalesChannelsTypes';

import preOrderSettingsSVG from '../../../assets/images/ico_preorder.svg';
import serviceChargeSVG from '../../../assets/images/service-charge.svg';
import { ReactComponent as TableSettingsIcon } from '../../../assets/images/tableSettings.svg';
import { KioskIcon } from '../../Kiosks/Components/KioskNestedMenuIcon';
import { getPhoneNumbersMenuItem } from '../../PhoneAgent/phoneNumbersPage';
import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from '../../RMS/utils/permissions';

const MenuItemSvgIcon = styled('img')({
  width: 24,
});

// Custom icon components
const PreOrderSettingsIcon = () => <MenuItemSvgIcon src={preOrderSettingsSVG} alt="" />;
const ServiceChargeIcon = () => <MenuItemSvgIcon src={serviceChargeSVG} alt="" />;

// Menu item templates to reduce object creation
const MENU_ITEM_TEMPLATES: Record<string, Omit<SettingsMenuItem, 'route'>> = {
  GENERAL_SETTINGS: {
    title: 'General_settings',
    path: '/edit',
    icon: <SettingsOutlinedIcon />,
    subtitle: 'Set_name_price_bands_and_menu_View_associated_bran',
    searchAlias: [
      'Channel name',
      'Sales channel name',
      'Assign menu',
      'Price bands',
      'Sales channel type',
      'Brand sales channel',
      'Phone number sales channel',
      'Store ID',
      'Edit restaurant name online order',
    ],
  },
  OPENING_HOURS: {
    title: 'Opening_Hours',
    path: '/opening-hours-v2',
    icon: <AccessTimeOutlinedIcon />,
    subtitle: 'Configure_sales_channel_opening_hours',
    searchAlias: [
      'Collection hours',
      'Delivery hours',
      'Sales channel opening hours',
      'Change hours online order',
      'Change opening hours app',
    ],
  },
  OPENING_HOURS_OVERRIDES: {
    title: 'Opening_Hours_Overrides',
    path: '/openinghouroverrides',
    icon: <AccessTimeOutlinedIcon />,
    subtitle: 'Configure_sales_channel_opening_hours_overrides',
    permissions: ['EditStores'],
    searchAlias: [
      'Override collection hours',
      'Override delivery hours',
      'Temporarily change opening hours',
      'Temporarily close restaurant',
      'Close property',
      'Close location',
      'Open a property',
      'Open a restaurant',
      'Set closing time',
      'Allow collection preorders while sales channel closed',
      'Allow delivery preorders while sales channel is closed',
    ],
  },
  DELIVERY_SETTINGS: {
    title: 'Delivery_settings_title',
    path: '/deliverysettings',
    icon: <LocalShippingOutlined />,
    subtitle: 'Set_delivery_zones_and_prices',
    permissions: ['ViewStores'],
    searchAlias: [
      'Enable delivery',
      'Delivery configuration online order',
      'Delivery settings online order',
      'Delivery settings app',
      'Delivery settings web',
      'Require customer name when ordering',
      'Set delivery zones',
      'Delivery map',
      'Delivery minimum order price',
      'Delivery minimum order amount',
      'Set delivery fee online order',
      'Accept cash for delivery',
      'Lead time settings',
      'SMS message for estimated delivery time',
      'Time to order accepted online order',
      'Web delivery settings',
      'App delivery settings',
      'Configure website delivery',
      'Configure app delivery',
    ],
  },
  COLLECTION: {
    title: 'Collection_settings_title',
    path: '/settings/collection',
    icon: <StoreOutlined />,
    subtitle: 'Collection_settings_description',
    permissions: ['ViewStores'],
    searchAlias: [
      'Enable collection',
      'Collection configuration online order',
      'Collection settings online order',
      'Collection settings app',
      'Collection settings web',
      'Require customer name when ordering',
      'Collection minimum order price',
      'Collection minimum order amount',
      'Set collection fee online order',
      'Accept cash for collection',
      'Lead time settings',
      'SMS message for estimated collection time',
      'Time to order accepted online order',
      'Web collection settings',
      'App collection settings',
      'Configure website collection',
      'Configure app collection',
    ],
  },
  ORDER_SETTINGS: {
    title: 'Order_settings_title',
    path: '/ordersettings',
    icon: <ShoppingCartOutlined />,
    subtitle: 'Order_settings_description',
    permissions: ['ViewStores'],
    searchAlias: [
      'Order configuration',
      'Customer notes',
      'Customer notes online order',
      'Chef notes on off',
      'Phone numbermask customer phone number on receipt',
      'Hide customer phone number on receipt',
      'Accept tips online',
      'Tipping rates',
      'Tip settings kiosk',
      'Tip settings online order',
      'Tip settings website',
      'Tip settings app',
      'Auto accept orders',
    ],
  },
  SERVICE_CHARGE: {
    title: 'Service_charge_title',
    path: '/servicecharge',
    icon: <ServiceChargeIcon />,
    subtitle: 'Service_charge_description',
    permissions: ['ViewStores'],
    searchAlias: [
      'Website service charge',
      'Online order service charge',
      'QR Order & Pay service charge',
      'App service charge',
    ],
  },
  ORDER_NOTIFICATIONS: {
    title: 'Order_notifications_title',
    path: '/ordernotifications',
    icon: <NotificationsOutlined />,
    subtitle: 'Order_notifications_description',
    permissions: ['ViewStores'],
    searchAlias: [
      'Notify orders sms',
      'Configure order notifications',
      'Email order notifications',
      'Setup order notifications',
      'Sms order notifications',
    ],
  },
  PRE_ORDER_SETTINGS: {
    title: 'Pre_order_settings_title',
    path: '/preordersettings',
    icon: <PreOrderSettingsIcon />,
    permissions: ['ViewStores'],
    searchAlias: [
      'Preorder for collection',
      'Preorder for delivery',
      'Customer order asap',
      'Always appear open',
      'Enforce cutoff time',
      'Online order preorder',
      'Apps preorder',
      'Preorder configuration',
      'Add preorder',
      'Customer can preorder',
      'Pre-order',
      'Lead time online order',
      'Interval online order',
      'Set capacity limits preorder',
      'Set preorder days',
      'Preorder cutoff time',
      'Preorder display time',
      'Preorder time',
      'Preorder display type',
      'Enable preorder',
      'Disable preorder',
    ],
  },
  KIOSK_SETTINGS: {
    title: 'Store_Kiosk_Settings',
    path: '/kiosksettings',
    icon: <KioskIcon />,
    subtitle: 'Configure_your_store_settings_for_any_associated_Kiosks',
    permissions: ['ViewStores'],
    searchAlias: [
      'Configure kiosk settings',
      'Kiosk logo',
      'Sales channel logo kiosk',
      'Require customer name kiosk',
      'Request customer phone number kiosk',
      'Request table number kiosk',
      'Offer dine in kiosk',
      'Offer sit in',
      'Request table number',
      'Request phone number kiosk',
      'Enter customer name kiosk',
    ],
  },
  TABLE_SETTINGS: {
    title: 'Table_settings_title',
    path: '/tablesettings',
    icon: <TableSettingsIcon />,
    subtitle: 'Table_settings_button_description',
    permissions: ['ViewStores'],
    searchAlias: [
      'Create table plan',
      'Table list',
      'Groups of tables',
      'Add table number',
      'Add table',
      'Table section',
      'Table group',
      'Restaurant layout',
      'Restaurant rooms',
      'Restaurant areas',
      'Customer select table when placing order',
      'Edit table number',
      'Edit table layout',
    ],
  },
  BANKING: {
    title: 'Banking_title',
    path: '/bankingsettings',
    icon: <AccountBalanceOutlined />,
    subtitle: 'Add_and_edit_bank_account_connected_to_this_sales',
    permissions: ['ViewAssignedBankAccount'],
    searchAlias: [
      'Assign bank account to sales channel',
      'Assign bank account to restaurant',
      'Manage bank account',
      'Update bank account online order',
      'Update bank account apps',
    ],
  },
  OPERATING_CONFIGURATION: {
    title: 'Operating_Configuration',
    path: '/operating-configuration',
    icon: <SettingsApplicationsOutlinedIcon />,
    subtitle: 'Configure_operating_configuration',
    permissions: ['EditStores'],
    searchAlias: [
      'Commission run schedule',
      'Commission rate',
      'Active queue activation time',
      'Dispatch options',
      'Delivery',
      'Rewards menu',
      'Enable Dine In',
      'Enable take away',
      'Enable collection',
      'Enable delivery',
      'Use own delivery driver',
      'Use own delivery rider',
      'Price band rewards menu',
    ],
  },
  TIMINGS_CONFIGURATION: {
    title: 'Timings_Configuration',
    path: '/timings-configuration',
    icon: <MoreTimeOutlinedIcon />,
    subtitle: 'Configure_timings_configuration',
    permissions: ['EditStores'],
    searchAlias: [
      'Default prep time',
      'Default delivery time',
      'Order interval',
      'Timing overrides',
      'Prep time',
      'Delivery time',
      'Dispatch type',
      'Order price',
      'Auto accept',
      'Remove timing overrides',
    ],
  },
  ORDER_INGEST: {
    title: 'Order_Ingest',
    path: `/order-ingest`,
    icon: <ZoomInMapOutlinedIcon />,
    subtitle: 'Configure_order_ingest',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
  },
  FEE_CONFIG: {
    title: 'View_and_Edit_Fees',
    path: '',
    icon: <AttachMoneyOutlinedIcon />,
    subtitle: 'Configure_fees_for_this_sales_channel',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
  },
  KIOSK_CARD_READER_BRANDING: {
    title: 'Sales_channel_customise_kiosk_card_readers_title',
    path: '',
    icon: <SmartphoneOutlinedIcon />,
    subtitle: 'Sales_channel_customise_kiosk_card_readers_description',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
  },
  DANGER_ZONE: {
    title: 'Danger_zone_title',
    path: '/dangerzone',
    icon: <Warning />,
    subtitle: 'Danger_zone_description',
    isDangerZone: true,
    permissions: ['Owner', 'FlipdishStaff'],
    searchAlias: [
      'Delete sales channel',
      'Delete store',
      'Archive store',
      'Archive sales channel',
      'Unpublish sales channel',
      'Unpublish online order',
      'Unpublish app restaurant option',
    ],
  },
};

// Helper function to create menu items with route
const createMenuItem = (
  template: SettingsMenuItem,
  baseUrl: string,
  commonData: {
    salesChannelType?: string;
    salesChannelName?: string;
    salesChannelId?: string;
    permissions?: AppState['permissions'];
  },
  overrides: Partial<SettingsMenuItem> = {}
): SettingsMenuItem => ({
  ...commonData,
  ...template,
  path: `${baseUrl}${template.path}`,
  ...overrides,
});

export const getFlipdishSalesChannelMenuItems = ({
  appId,
  baseUrl,
  salesChannelType,
  storefrontInformation,
  linkedSalesChannelId,
  isOldStorePage = false,
  permissions,
  isSearchItems = false,
  isShowCollectionSettingsAtPropertyLevelOn = false,
  storeId,
  propertyId,
  isPhoneNumbersPageOn = false,
}: {
  appId?: string;
  baseUrl: string;
  salesChannelType: SalesChannelTypeEnum;
  storefrontInformation: any;
  linkedSalesChannelId?: string;
  isOldStorePage?: boolean;
  permissions?: AppState['permissions'];
  isSearchItems?: boolean;
  isShowCollectionSettingsAtPropertyLevelOn?: boolean;
  storeId?: string;
  propertyId?: string;
  isPhoneNumbersPageOn?: boolean;
}): SettingsMenuItem[] => {
  const menuItems: SettingsMenuItem[] = [];

  const isHidden = !(
    permissions?.some((permission) =>
      [
        'Owner',
        'ManagedOwner',
        'FlipdishStaff',
        ...propertyManagerRolePermission,
        ...propertyOwnerRolePermission,
      ].includes(permission)
    ) ?? false
  );

  const commonData = {
    salesChannelType: getSalesChannelType(salesChannelType, false),
    salesChannelName: storefrontInformation?.name,
    salesChannelId: storefrontInformation?.code,
    permissions: salesChannelPermissions,
  };
  // General settings (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.GENERAL_SETTINGS, baseUrl, commonData));

  // Phone numbers page (replaces the phone field on General settings, so shares its visibility)
  isPhoneNumbersPageOn &&
    appId &&
    propertyId &&
    !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(getPhoneNumbersMenuItem({ appId, propertyId, ...commonData }));

  const isPhoneAgentChannel =
    salesChannelType === SalesChannelTypeEnum.FLIPDISH_PHONE_AGENT ||
    salesChannelType === SalesChannelTypeEnum.PhoneAgent;

  // Opening hours with conditional logic (hidden for linked sales channels)
  const openingHoursRoute = !window.location.pathname?.includes('storegroups')
    ? '/opening-hours-v2'
    : '/openinghours';

  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.OPENING_HOURS, baseUrl, commonData, {
        path: `${baseUrl}${openingHoursRoute}`,
      })
    );

  // Opening hour overrides (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.OPENING_HOURS_OVERRIDES, baseUrl, commonData)
    );

  // Delivery settings (hidden for kiosk paths, insufficient permissions, or linked sales channels)
  !(salesChannelType === SalesChannelTypeEnum.FLIPDISH_KIOSK || isHidden || linkedSalesChannelId) &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.DELIVERY_SETTINGS, baseUrl, commonData));

  // Collection settings (hidden for linked sales channels)
  const collectionSettingsRoute = isShowCollectionSettingsAtPropertyLevelOn
    ? '/settings/collection'
    : '/collection';
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.COLLECTION, baseUrl, commonData, {
        hidden: isHidden,
        path: `${baseUrl}${collectionSettingsRoute}`,
      })
    );

  // Order settings (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.ORDER_SETTINGS, baseUrl, commonData));

  // Service charge (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.SERVICE_CHARGE, baseUrl, commonData));

  // Order notifications (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.ORDER_NOTIFICATIONS, baseUrl, commonData));

  // Pre-order settings (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.PRE_ORDER_SETTINGS, baseUrl, commonData));

  // Kiosk settings (hidden for linked sales channels or Phone Agent channels)
  !isPhoneAgentChannel &&
    !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.KIOSK_SETTINGS, baseUrl, commonData));

  // Table settings (hidden for linked sales channels or Phone Agent channels)
  !isPhoneAgentChannel &&
    !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.TABLE_SETTINGS, baseUrl, commonData));

  // Banking settings (hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.BANKING, baseUrl, commonData));

  // Operating configuration (hidden for old store pages, insufficient permissions, or linked sales channels).
  // Shown for Phone Agent: RMS Driver Sales only includes delivery sales from channels set to use own delivery riders here.
  !(isOldStorePage || isHidden || linkedSalesChannelId) &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.OPERATING_CONFIGURATION, baseUrl, commonData)
    );

  // Timings configuration (hidden for old store pages, Phone Agent channels, insufficient permissions, or linked sales channels)
  !(isPhoneAgentChannel || isOldStorePage || isHidden || linkedSalesChannelId) &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.TIMINGS_CONFIGURATION, baseUrl, commonData));

  // Order ingest
  !isHidden &&
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.ORDER_INGEST, baseUrl, commonData));

  // View and edit fees (Flipdish staff only, hidden for linked sales channels)
  !isHidden &&
    !linkedSalesChannelId &&
    !!appId &&
    !!storeId &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.FEE_CONFIG, baseUrl, commonData, {
        path: `/${appId}/bulk-updater/fee-config/${storeId}`,
      })
    );

  // Kiosk card reader branding (Flipdish staff only, Flipdish Kiosk channels only, hidden for
  // linked sales channels). Opens the marketing page that brands this store's kiosk card readers.
  salesChannelType === SalesChannelTypeEnum.FLIPDISH_KIOSK &&
    !isHidden &&
    !linkedSalesChannelId &&
    !!appId &&
    !!propertyId &&
    !!storeId &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.KIOSK_CARD_READER_BRANDING, baseUrl, commonData, {
        path: `/${appId}/marketing/${propertyId}/kiosk-readers?storeId=${encodeURIComponent(storeId)}`,
      })
    );

  // Danger zone with conditional description
  const dangerZoneDescription = !linkedSalesChannelId
    ? 'Danger_zone_description'
    : 'Danger_zone_description_2';

  !isHidden &&
    !isSearchItems &&
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.DANGER_ZONE, baseUrl, commonData, {
        subtitle: dangerZoneDescription,
      })
    );

  return menuItems;
};
